import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { SET_REGISTRY } from './setRegistry';
import { getParallelsForType } from './parallelData';
import { getParallelsForSet } from './sets/parallelsConfig';
import { imageUrl, isLandscapeCard } from './CardImage';
import { buildTilesForCard, PARALLEL_ALL } from './Gallery';
import { lightAccent } from './theme';
import GalleryLightbox from './GalleryLightbox';

const STATE_COLORS = {
  collected: 'var(--green)',
  wishlist: 'var(--gold)',
  trade: 'var(--cyan)',
  for_sale: 'var(--purple)',
};

function GalleryCard({ card, cardKey, userId, state, parallel, printRun, onView, onUrl }) {
  // Extract serial number if this key includes __s__N
  const serialMatch = cardKey.match(/__s__(\d+)$/);
  const serialNum = serialMatch ? serialMatch[1] : null;
  const serialDisplay = (serialNum && state !== "wishlist") ? (printRun ? `#${serialNum}/${printRun}` : `#${serialNum}`) : null;
  const [frontUrl, setFrontUrl] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Resolved URL (or null) is reported up so the shared lightbox can page
    // across every tile with a photo, not just this one.
    const settle = (url) => { setFrontUrl(url); setLoaded(true); onUrl?.(cardKey, url); };
    const primaryUrl = imageUrl(userId, cardKey, 'front');
    const img = new Image();
    img.onload = () => settle(primaryUrl);
    img.onerror = () => {
      // If this is a serial key, try the base parallel URL once
      if (cardKey.includes('__s__')) {
        const baseKey = cardKey.split('__s__')[0];
        const fallbackUrl = imageUrl(userId, baseKey, 'front');
        const img2 = new Image();
        img2.onload = () => settle(fallbackUrl);
        img2.onerror = () => settle(null);
        img2.src = fallbackUrl;
      } else {
        settle(null);
      }
    };
    img.src = primaryUrl;
  }, [cardKey, userId]);

  return (
    <>
      <div style={styles.card} onClick={() => frontUrl && onView()}>
        <div style={styles.cardImageWrap}>
          {!loaded && <div style={styles.cardPlaceholder} />}
          {loaded && frontUrl && (
            <img src={frontUrl} alt={card.name} style={{ ...styles.cardImage, ...(isLandscapeCard(cardKey) ? { objectFit: 'contain' } : {}) }} />
          )}
          {loaded && !frontUrl && (
            <div style={styles.cardMissing}>
              <svg viewBox="0 0 100 140" style={{ width: '100%', height: '100%', opacity: 0.15 }}>
                <line x1="5" y1="5" x2="95" y2="135" stroke="var(--text)" strokeWidth="2"/>
                <line x1="95" y1="5" x2="5" y2="135" stroke="var(--text)" strokeWidth="2"/>
                <rect x="2" y="2" width="96" height="136" rx="4" fill="none" stroke="var(--border)" strokeWidth="1.5"/>
              </svg>
            </div>
          )}
          {state && <div style={{ ...styles.statePip, background: STATE_COLORS[state] }} />}
        </div>
        <div style={styles.cardCaption}>
          <div style={styles.cardNum}>{card.n}{serialDisplay ? ` · ${serialDisplay}` : ''}</div>
          <div style={styles.cardName}>{card.name}</div>
          {parallel && (
            <div style={{ marginTop: '3px' }}>
              <span style={{ fontFamily: "'Orbitron',monospace", fontSize: '7px', letterSpacing: '0.05em', padding: '1px 5px', border: '1px solid', borderRadius: '2px', borderColor: lightAccent(parallel.color || '#c8d8f0') + '66', color: lightAccent(parallel.color || '#c8d8f0'), background: lightAccent(parallel.color || '#c8d8f0') + '12' }}>
                {parallel.name}{parallel.print_run ? ` /${parallel.print_run}` : ''}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function PublicGallery({ username, setId }) {
  const [profile, setProfile] = useState(null);
  const [cardStates, setCardStates] = useState({});
  const [activeSets, setActiveSets] = useState([]); // [{ meta, subsets }]
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // The public gallery is a read-only showcase: what's displayed is fixed by the
  // share URL. Recipients don't get interactive filter controls, so these are
  // plain derived values rather than state.
  const urlParams = new URLSearchParams(window.location.search);
  const cardFilter = urlParams.get('filter') || 'all';
  const selectedTypes = urlParams.get('types') ? urlParams.get('types').split(',') : ['base'];
  const parallelFilter = urlParams.get('parallel') === 'all' ? PARALLEL_ALL : (urlParams.get('parallel') || 'base');
  const [copied, setCopied] = useState(false);
  // Shared lightbox: index into allCards | null. Each card reports its
  // resolved front URL into the ref so the lightbox can page across tiles.
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const tileUrls = useRef({});

  const resolveMeta = (id) =>
    SET_REGISTRY.find(s => s.id === id) ||
    SET_REGISTRY.find(s => s.id === id.replace(/^([a-z-]+)-(\d{4})$/, '$2-$1')) ||
    null;

  const setMeta = resolveMeta(setId) || SET_REGISTRY[0];
  // Sets to display: explicit `sets` param (multi-set) wins, else the path set.
  const setsParam = urlParams.get('sets');
  const multiSet = !!setsParam && setsParam.split(',').filter(Boolean).length > 1;

  useEffect(() => {
    const load = async () => {
      // Load profile first — public_profiles only contains is_public rows.
      const { data: profileData, error } = await supabase
        .from('public_profiles')
        .select('*')
        .eq('username', username.toLowerCase())
        .single();

      if (error || !profileData || !profileData.is_public) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setProfile(profileData);

      // Resolve the list of sets to display, then load each set's card data
      // and this user's collection rows for it. Card keys are namespaced by
      // set id, so the merged states map never collides.
      const idList = setsParam ? setsParam.split(',').filter(Boolean) : [setMeta.id];
      const metas = idList.map(resolveMeta).filter(Boolean);
      const loaded = [];
      const merged = {};
      await Promise.all(metas.map(async (meta) => {
        try {
          const module = await import(`./sets/${meta.dataFile}.js`);
          loaded.push({ meta, subsets: module.SETS || [] });
        } catch (e) { console.error(e); }
        const { data } = await supabase
          .from('collections')
          .select('card_key, status')
          .eq('user_id', profileData.id)
          .like('card_key', `${meta.id}__%`);
        (data || []).forEach(row => { merged[row.card_key] = row.status; });
      }));

      setActiveSets(loaded);
      setCardStates(merged);
      setLoading(false);
    };
    load();
  }, [username, setsParam, setMeta.id]);

  // Build visible cards across every active set.
  const allCards = activeSets.flatMap(s =>
    s.subsets.filter(ss => selectedTypes.includes(ss.type)).flatMap(ss => {
      const parallels = getParallelsForSet(s.meta.id, ss.type, ss) || getParallelsForType(ss.type) || [];
      return ss.cards.flatMap(card =>
        buildTilesForCard(s.meta.id, ss.id, card, parallels, parallelFilter, cardStates)
      );
    })
  ).filter(({ state }) => {
    if (cardFilter === 'all') return true;
    return state === cardFilter;
  });

  const shareUrl = window.location.href;
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '12px', letterSpacing: '4px', color: 'var(--muted-2)' }}>LOADING GALLERY...</div>
    </div>
  );

  if (notFound) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: "'Orbitron', monospace", fontSize: '12px', letterSpacing: '4px', color: 'var(--muted-2)' }}>GALLERY NOT FOUND</div>
    </div>
  );

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div>
          <div style={styles.logoLine}>{multiSet ? `${activeSets.length || setsParam.split(',').filter(Boolean).length} COLLECTIONS` : `${setMeta.brand} · ${setMeta.category} · ${setMeta.year}`}</div>
          <h1 style={styles.title}><span style={{ color: setMeta.color }}>{profile.username}</span>'s GALLERY</h1>
        </div>
        <div style={styles.headerRight}>
          <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" style={{ ...styles.shareBtn, background: '#25d366' }}>WhatsApp</a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" style={{ ...styles.shareBtn, background: '#1877f2' }}>Facebook</a>
          <button style={{ ...styles.shareBtn, background: '#5865f2', cursor: 'pointer' }} onClick={() => { navigator.clipboard.writeText(`Check out my gallery!\n${shareUrl}`); alert('Copied for Discord!'); }}>Discord</button>
          <button style={{ ...styles.shareBtn, background: 'var(--muted-2)', cursor: 'pointer' }} onClick={copyLink}>{copied ? '✓ COPIED!' : 'Copy Link'}</button>
          <a href="/" style={{ ...styles.shareBtn, background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted-2)' }}>← My Collection</a>
          <a href={`/u/${username}`} style={{ ...styles.shareBtn, background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted-2)' }}>View Profile →</a>
        </div>
      </header>

      {/* Read-only summary banner — recipients see what's on display, not filters */}
      <div style={styles.lockedBanner}>
        <span style={styles.lockedText}>
          {cardFilter !== 'all' ? cardFilter.toUpperCase().replace('_', ' ') : 'ALL CARDS'} · {selectedTypes.map(t => t.toUpperCase()).join(', ')}{parallelFilter === PARALLEL_ALL ? ' · ALL PARALLELS' : parallelFilter !== 'base' ? ` · ${parallelFilter.toUpperCase()} PARALLEL` : ''}{multiSet ? ` · ${activeSets.length} COLLECTIONS` : ''}
        </span>
        <span style={{ ...styles.lockedText, color: 'var(--muted-2)', marginLeft: '12px' }}>{allCards.length} cards</span>
      </div>

      {/* Grid */}
      <div style={styles.grid}>
        {allCards.length === 0 ? (
          <div style={styles.empty}>No cards match your filters</div>
        ) : (
          allCards.map(({ card, cardKey, state, parallel }, i) => (
            <GalleryCard
              key={cardKey}
              card={card}
              cardKey={cardKey}
              userId={profile.id}
              state={state}
              parallel={parallel}
              printRun={parallel?.print_run}
              onView={() => setLightboxIdx(i)}
              onUrl={(key, url) => { tileUrls.current[key] = url; }} />
          ))
        )}
      </div>

      {lightboxIdx != null && (
        <GalleryLightbox
          tiles={allCards}
          index={lightboxIdx}
          urlFor={(t) => tileUrls.current[t.cardKey]}
          captionFor={({ card, cardKey, state, parallel }) => {
            // Mirrors the tile caption: serial (hidden on wishlist rows) +
            // parallel chip text + card name.
            const serialNum = cardKey.match(/__s__(\d+)$/)?.[1] || null;
            const serialDisplay = (serialNum && state !== 'wishlist')
              ? (parallel?.print_run ? `#${serialNum}/${parallel.print_run}` : `#${serialNum}`)
              : null;
            return `${card.n}${serialDisplay ? ` · Serial ${serialDisplay}` : ''}${parallel ? ` · ${parallel.name}${parallel.print_run ? ` /${parallel.print_run}` : ''}` : ''} — ${card.name}`;
          }}
          onNavigate={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0, flexWrap: 'wrap', gap: '12px' },
  logoLine: { fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '4px', color: 'var(--brand-gold)', marginBottom: '4px' },
  title: { fontFamily: "'Orbitron', monospace", fontSize: '20px', fontWeight: 900, letterSpacing: '3px', color: 'var(--text)', margin: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  shareBtn: { fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1px', padding: '6px 12px', border: 'none', borderRadius: '3px', color: '#fff', textDecoration: 'none', cursor: 'pointer' },
  filters: { display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', padding: '12px 24px', borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)', flexShrink: 0 },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  filterLabel: { fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '2px', color: 'var(--muted)', marginRight: '2px' },
  filterBtn: { fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', padding: '4px 10px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted-2)', cursor: 'pointer', borderRadius: '2px' },
  filterBtnActive: { borderColor: 'var(--cyan)', color: 'var(--cyan)', background: 'rgba(var(--cyan-rgb),0.08)' },
  cardCount: { fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: 'var(--muted)', marginLeft: 'auto' },
  grid: { flex: 1, padding: '24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '20px', alignContent: 'start' },
  card: { display: 'flex', flexDirection: 'column', gap: '8px', cursor: 'pointer' },
  cardImageWrap: { position: 'relative', width: '100%', paddingBottom: '140%', background: 'var(--panel-deep)', border: '1px solid var(--border)', borderRadius: '4px', overflow: 'hidden' },
  cardPlaceholder: { position: 'absolute', inset: 0, background: 'var(--panel-deep)' },
  cardImage: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },
  cardMissing: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' },
  statePip: { position: 'absolute', top: '6px', right: '6px', width: '8px', height: '8px', borderRadius: '50%' },
  cardCaption: { textAlign: 'center' },
  cardNum: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: 'var(--muted)' },
  cardName: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: 'var(--text)', lineHeight: 1.3 },
  lockedBanner: { padding: '10px 24px', borderBottom: '1px solid var(--border)', background: 'rgba(var(--cyan-rgb),0.04)', display: 'flex', alignItems: 'center' },
  lockedText: { fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--cyan)' },
  empty: { gridColumn: '1/-1', textAlign: 'center', fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', color: 'var(--muted)', padding: '60px 0' },
};
